import { type PermissionProps, useHasPermissions } from "@/components/misc";
import { SidebarMenuBadge, SidebarMenuButton, SidebarMenuItem } from "@/components/ui/sidebar";
import { type FileRoutesByTo } from "@/routeTree.gen";
import { Link, useMatchRoute } from "@tanstack/react-router";
import { type LucideIcon } from "lucide-react";

export type CustomLinkProps = {
  to: keyof FileRoutesByTo;
  icon: LucideIcon;
  label: string;
  badge?: string;
  activeTo?: ReadonlyArray<keyof FileRoutesByTo>;
} & PermissionProps;

export const SidebarLink: React.FC<CustomLinkProps> = ({
  to,
  icon: Icon,
  label,
  badge,
  activeTo,
  ...permissions
}) => {
  const matchRoute = useMatchRoute();
  const hasPermission = useHasPermissions(permissions);
  if (!hasPermission && permissions.requires !== undefined) return null;

  const targets = activeTo ?? [to];
  const isActive = targets.some((target) =>
    Boolean(
      matchRoute({
        to: target,
        fuzzy: target !== "/",
      }),
    )
  );

  return (
    <SidebarMenuItem key={to}>
      <SidebarMenuButton
        isActive={isActive}
        tooltip={label}
        render={
          <Link
            to={to}
            className="w-full min-w-0 active:scale-95 hover:bg-accent hover:text-accent-foreground"
          />
        }
      >
        <Icon className="size-5" />
        <span className="min-w-0 flex-1 truncate whitespace-nowrap overflow-hidden">
          {label}
        </span>
      </SidebarMenuButton>
      {badge !== undefined && <SidebarMenuBadge>{badge}</SidebarMenuBadge>}
    </SidebarMenuItem>
  );
};
